import React, { useCallback, useLayoutEffect, useRef } from "react";
import CaseIdBadge from "../../../shared/components/CaseIdBadge";
import EnterpriseFilterSelect from "../../../shared/components/EnterpriseFilterSelect";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import AdminDeletionCaseReviewList from "./AdminDeletionCaseReviewList";
import { formatEuro } from "../../../shared/utils/deletionCasePricing";
import { getAdminStatusOptions, packageLabelForAccountStatus } from "../adminApi";
import { useTranslation } from "../../../i18n";

/** Sticky left columns: expand toggle + case ID stay fixed while the rest scrolls. */
const STICKY_EXPAND_LEFT = 0;
const STICKY_CASE_ID_LEFT = 52;
const STICKY_SHADOW = "shadow-[4px_0_12px_-4px_rgba(15,23,42,0.14)]";

const TABLE_CLASS =
  "w-full min-w-[1320px] table-fixed border-separate border-spacing-0 text-left";

const SCROLL_LANE_CLASS = "w-full overflow-x-auto app-scroll-x-hidden";

function getScrollLanes(scrollLanesRef) {
  return [
    scrollLanesRef.current.header,
    ...Object.values(scrollLanesRef.current.rows),
  ].filter(Boolean);
}

function stickyHeaderClass(left, withShadow = false) {
  return [
    "sticky z-30 bg-surface-container-low",
    left === STICKY_EXPAND_LEFT ? "left-0" : "left-[52px]",
    withShadow ? STICKY_SHADOW : "",
  ].join(" ");
}

function stickyBodyClass(left, { expanded = false, withShadow = false } = {}) {
  const bg = expanded ? "bg-surface-container-low" : "bg-surface-container-lowest";
  return [
    "sticky z-20",
    bg,
    "group-hover:bg-surface-container-low",
    left === STICKY_EXPAND_LEFT ? "left-0" : "left-[52px]",
    withShadow ? STICKY_SHADOW : "",
    expanded && left === STICKY_EXPAND_LEFT ? "border-l-4 border-l-secondary" : "",
  ].join(" ");
}

const statusBadgeClass = (status) => {
  switch (status) {
    case "completed":
      return "bg-emerald-50 text-emerald-800 ring-emerald-200";
    case "rejected":
      return "bg-red-50 text-red-800 ring-red-200";
    case "legal_action":
      return "bg-amber-50 text-amber-900 ring-amber-200";
    case "started":
      return "bg-sky-50 text-sky-800 ring-sky-200";
    default:
      return "bg-secondary/10 text-secondary ring-secondary/20";
  }
};

const packageBadgeClass = (accountStatus) =>
  accountStatus === "paid"
    ? "bg-secondary/10 text-secondary ring-secondary/20"
    : "bg-amber-50 text-amber-900 ring-amber-200";

function formatDateSummary(iso, locale) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale === "de" ? "de-DE" : "en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const TableColgroup = () => (
  <colgroup>
    <col className="w-[52px]" />
    <col className="w-[200px]" />
    <col className="w-[140px]" />
    <col className="w-[200px]" />
    <col className="w-[150px]" />
    <col className="w-[130px]" />
    <col className="w-[120px]" />
    <col className="w-[80px]" />
    <col className="w-[100px]" />
    <col className="w-[160px]" />
    <col className="w-[150px]" />
    <col className="w-[100px]" />
  </colgroup>
);

const Th = ({ children, className = "" }) => (
  <th
    className={`whitespace-nowrap px-3 py-3 text-left text-label-sm font-semibold uppercase tracking-wide text-on-surface-variant ${className}`}
  >
    {children}
  </th>
);

const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-3 align-middle text-body-md ${className}`}>{children}</td>
);

const AdminDeletionCasesTable = ({
  cases,
  expandedId,
  onToggleExpand,
  onStatusChange,
  onSaveOutcomes,
  updatingId,
}) => {
  const { t, locale } = useTranslation();
  const statusOptions = React.useMemo(() => getAdminStatusOptions(t), [t]);
  const scrollLanesRef = useRef({ header: null, rows: {} });
  const scrollRailWrapRef = useRef(null);
  const scrollRailRef = useRef(null);
  const scrollThumbRef = useRef(null);
  const scrollMetricsRef = useRef({ scrollWidth: 0, clientWidth: 0, scrollLeft: 0 });
  const dragStateRef = useRef(null);
  const isSyncingScrollRef = useRef(false);

  const updateScrollRail = useCallback((sourceEl) => {
    const lanes = getScrollLanes(scrollLanesRef);
    const el = sourceEl || lanes[0];
    const thumb = scrollThumbRef.current;
    const wrap = scrollRailWrapRef.current;
    if (!el || !thumb) return;

    const { scrollWidth, clientWidth, scrollLeft } = el;
    scrollMetricsRef.current = { scrollWidth, clientWidth, scrollLeft };

    const overflow = scrollWidth > clientWidth + 2;
    if (wrap) wrap.hidden = !overflow;
    if (!overflow) return;

    const trackWidth = scrollRailRef.current?.clientWidth || clientWidth;
    const ratio = clientWidth / scrollWidth;
    const thumbWidthPx = Math.max(ratio * trackWidth, 48);
    const maxScroll = scrollWidth - clientWidth;
    const maxThumbTravel = Math.max(trackWidth - thumbWidthPx, 1);
    const thumbLeftPx = maxScroll > 0 ? (scrollLeft / maxScroll) * maxThumbTravel : 0;

    thumb.style.width = `${thumbWidthPx}px`;
    thumb.style.left = `${thumbLeftPx}px`;
  }, []);

  const applyScrollLeft = useCallback(
    (left) => {
      const lanes = getScrollLanes(scrollLanesRef);
      const primary = lanes[0];
      if (!primary) return;

      const maxScroll = Math.max(0, primary.scrollWidth - primary.clientWidth);
      const clamped = Math.min(Math.max(0, left), maxScroll);

      isSyncingScrollRef.current = true;
      lanes.forEach((lane) => {
        lane.scrollLeft = clamped;
      });
      isSyncingScrollRef.current = false;
      updateScrollRail(primary);
    },
    [updateScrollRail]
  );

  const syncHorizontalScroll = useCallback(
    (sourceEl) => {
      if (!sourceEl || isSyncingScrollRef.current) return;

      const left = sourceEl.scrollLeft;
      const lanes = getScrollLanes(scrollLanesRef);

      if (lanes.every((lane) => lane.scrollLeft === left)) {
        updateScrollRail(sourceEl);
        return;
      }

      isSyncingScrollRef.current = true;
      lanes.forEach((lane) => {
        if (lane !== sourceEl) lane.scrollLeft = left;
      });
      isSyncingScrollRef.current = false;
      updateScrollRail(sourceEl);
    },
    [updateScrollRail]
  );

  const handleHeaderScroll = useCallback(
    (event) => syncHorizontalScroll(event.currentTarget),
    [syncHorizontalScroll]
  );

  const handleRowScroll = useCallback(
    (event) => syncHorizontalScroll(event.currentTarget),
    [syncHorizontalScroll]
  );

  const setHeaderScrollRef = useCallback(
    (node) => {
      scrollLanesRef.current.header = node;
      if (node) requestAnimationFrame(() => updateScrollRail(node));
    },
    [updateScrollRail]
  );

  const setRowScrollRef = useCallback(
    (caseId) => {
      return (node) => {
        if (node) scrollLanesRef.current.rows[caseId] = node;
        else delete scrollLanesRef.current.rows[caseId];

        const header = scrollLanesRef.current.header;
        if (header) requestAnimationFrame(() => updateScrollRail(header));
      };
    },
    [updateScrollRail]
  );

  useLayoutEffect(() => {
    const node = scrollLanesRef.current.header;
    if (!node) return undefined;

    const refresh = () => updateScrollRail(node);
    refresh();

    const observer = new ResizeObserver(refresh);
    observer.observe(node);
    window.addEventListener("resize", refresh);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", refresh);
    };
  }, [cases.length, expandedId, updateScrollRail]);

  const handleTrackClick = useCallback(
    (event) => {
      if (event.target === scrollThumbRef.current) return;
      const track = scrollRailRef.current;
      if (!track) return;

      const rect = track.getBoundingClientRect();
      const { scrollWidth, clientWidth } = scrollMetricsRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const ratio = (event.clientX - rect.left) / rect.width;
      applyScrollLeft(ratio * maxScroll);
    },
    [applyScrollLeft]
  );

  const handleThumbPointerDown = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    dragStateRef.current = {
      startX: event.clientX,
      startScrollLeft: scrollMetricsRef.current.scrollLeft,
    };
    scrollThumbRef.current?.setPointerCapture(event.pointerId);
  }, []);

  const handleThumbPointerMove = useCallback(
    (event) => {
      if (!dragStateRef.current) return;

      const { scrollWidth, clientWidth } = scrollMetricsRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const trackWidth = scrollRailRef.current?.clientWidth || 1;
      const ratio = clientWidth / scrollWidth;
      const thumbWidthPx = Math.max(ratio * trackWidth, 48);
      const maxThumbTravel = Math.max(trackWidth - thumbWidthPx, 1);
      const deltaX = event.clientX - dragStateRef.current.startX;
      const scrollDelta = (deltaX / maxThumbTravel) * maxScroll;
      applyScrollLeft(dragStateRef.current.startScrollLeft + scrollDelta);
    },
    [applyScrollLeft]
  );

  const handleThumbPointerUp = useCallback(() => {
    dragStateRef.current = null;
  }, []);

  return (
    <div
      id="admin-deletion-cases-table"
      className="w-full rounded-2xl border border-outline-variant/30 bg-surface-container-lowest shadow-soft"
    >
      <div
        ref={setHeaderScrollRef}
        onScroll={handleHeaderScroll}
        className={`${SCROLL_LANE_CLASS} border-b border-outline-variant/30`}
      >
        <table className={TABLE_CLASS}>
          <TableColgroup />
          <thead className="bg-surface-container-low">
            <tr>
              <Th className={stickyHeaderClass(STICKY_EXPAND_LEFT)} aria-hidden />
              <Th className={stickyHeaderClass(STICKY_CASE_ID_LEFT, true)}>
                {t("adminTable.caseId")}
              </Th>
              <Th>{t("adminTable.user")}</Th>
              <Th>{t("adminTable.email")}</Th>
              <Th>{t("adminTable.company")}</Th>
              <Th>{t("adminTable.package")}</Th>
              <Th>{t("adminTable.violation")}</Th>
              <Th className="text-center">{t("adminTable.reviews")}</Th>
              <Th className="text-right">{t("adminTable.amount")}</Th>
              <Th>{t("adminTable.status")}</Th>
              <Th>{t("adminTable.created")}</Th>
              <Th className="pr-4 text-right">{t("adminTable.action")}</Th>
            </tr>
          </thead>
        </table>
      </div>

      {cases.map((c) => {
        const expanded = expandedId === c.id;
        const total =
          c.pricing != null && typeof c.pricing.caseTotal === "number"
            ? formatEuro(c.pricing.caseTotal)
            : "—";

        return (
          <div key={c.id} className="border-b border-outline-variant/20 last:border-b-0">
            <div
              ref={setRowScrollRef(c.id)}
              onScroll={handleRowScroll}
              className={SCROLL_LANE_CLASS}
            >
              <table className={TABLE_CLASS}>
                <TableColgroup />
                <tbody>
                  <tr
                    className={[
                      "group transition-colors",
                      expanded
                        ? "bg-surface-container-low"
                        : "hover:bg-surface-container-lowest/80",
                    ].join(" ")}
                  >
                    <Td className={`text-center ${stickyBodyClass(STICKY_EXPAND_LEFT, { expanded })}`}>
                      <button
                        type="button"
                        onClick={() => onToggleExpand(c.id)}
                        className="mx-auto flex h-8 w-8 items-center justify-center rounded-lg border border-outline-variant/40 bg-white text-secondary transition hover:bg-secondary/5"
                        aria-expanded={expanded}
                        aria-label={
                          expanded
                            ? t("adminTable.collapseDetails")
                            : t("adminTable.expandDetails")
                        }
                      >
                        <MaterialIcon
                          name={expanded ? "expand_more" : "chevron_right"}
                          className="text-[20px]"
                        />
                      </button>
                    </Td>

                    <Td className={stickyBodyClass(STICKY_CASE_ID_LEFT, { expanded, withShadow: true })}>
                      <CaseIdBadge caseData={c} showCopy />
                    </Td>

                    <Td>
                      <span
                        className="block truncate font-semibold text-primary"
                        title={c.submitter.fullName || ""}
                      >
                        {c.submitter.fullName || "—"}
                      </span>
                    </Td>

                    <Td>
                      <span
                        className="block truncate text-on-surface-variant"
                        title={c.submitter.email || ""}
                      >
                        {c.submitter.email || "—"}
                      </span>
                    </Td>

                    <Td>
                      <span
                        className="block truncate text-on-surface-variant"
                        title={c.submitter.companyName || ""}
                      >
                        {c.submitter.companyName || "—"}
                      </span>
                    </Td>

                    <Td>
                      <span
                        className={`inline-flex max-w-full truncate rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${packageBadgeClass(c.submitter.accountStatus)}`}
                        title={packageLabelForAccountStatus(c.submitter.accountStatus, t)}
                      >
                        {packageLabelForAccountStatus(c.submitter.accountStatus, t)}
                      </span>
                    </Td>

                    <Td>
                      <span
                        className="block truncate text-on-surface-variant"
                        title={c.violationType || ""}
                      >
                        {c.violationType || "—"}
                      </span>
                    </Td>

                    <Td className="text-center tabular-nums font-semibold text-primary">
                      {c.reviewCount}
                    </Td>

                    <Td className="text-right tabular-nums font-semibold text-primary">
                      {total}
                    </Td>

                    <Td className="relative overflow-visible">
                      <EnterpriseFilterSelect
                        value={c.status}
                        onChange={(value) => onStatusChange(c.id, value)}
                        options={statusOptions}
                        disabled={updatingId === c.id}
                        minWidth="168px"
                        fullWidth
                        usePortal
                        preferPlacement="auto"
                        stopPropagation
                        buttonClassName={`flex h-9 w-full items-center justify-between gap-1 rounded-full border-0 px-3 text-[12px] font-semibold ring-1 ring-inset transition-all hover:ring-secondary/40 focus:outline-none focus:ring-2 focus:ring-secondary disabled:opacity-60 ${statusBadgeClass(c.status)}`}
                        ariaLabel={t("adminTable.statusForCase", {
                          caseId: c.caseReference,
                        })}
                      />
                    </Td>

                    <Td className="whitespace-nowrap text-label-sm text-on-surface-variant">
                      {formatDateSummary(c.createdAt, locale)}
                    </Td>

                    <Td className="pr-4 text-right">
                      <button
                        type="button"
                        onClick={() => onToggleExpand(c.id)}
                        className="whitespace-nowrap font-label-sm font-bold text-primary transition hover:text-secondary"
                      >
                        {t("adminTable.manage")}
                      </button>
                    </Td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              className={`grid bg-[#f8fafb] transition-[grid-template-rows] duration-300 ease-out ${
                expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
              }`}
            >
              <div className="overflow-hidden">
                <div className="border-t border-outline-variant/20 px-4 py-5 md:px-6">
                  <AdminDeletionCaseReviewList
                    key={`${c.id}-${c.updatedAt ?? ""}`}
                    caseData={c}
                    onSaveOutcomes={onSaveOutcomes}
                    saving={updatingId === c.id}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div
        ref={scrollRailWrapRef}
        hidden
        className="border-t border-outline-variant/20 bg-surface-container-low/30 px-4 py-3 md:px-6"
      >
        <div
          ref={scrollRailRef}
          className="app-scroll-rail mx-auto w-full max-w-full"
          onClick={handleTrackClick}
          role="scrollbar"
          aria-orientation="horizontal"
          aria-controls="admin-deletion-cases-table"
        >
          <div
            ref={scrollThumbRef}
            className="app-scroll-rail__thumb"
            onPointerDown={handleThumbPointerDown}
            onPointerMove={handleThumbPointerMove}
            onPointerUp={handleThumbPointerUp}
            onPointerCancel={handleThumbPointerUp}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDeletionCasesTable;
